import { Environment, PerformanceMonitor, useGLTF } from "@react-three/drei";
import { LevaWrapper, AudioManager, KeyboardMapper, KTX2Preloader, preloadVATAssets } from "@core";
import { Canvas, useLoader } from "@react-three/fiber";
import { useEffect, Suspense, useMemo, useState } from "react";
import { DirectionalLight } from "../components/DirectionalLight";
import { WebGPURenderer } from "three/webgpu";
import Effects from "../components/Effects/Effects";
import { useGameStore } from "../core/store/gameStore";
import { CameraViewControl } from "../components/camera/CameraViewControl";
import { DeviceDetector } from "../core/utils/DeviceDetector";
import { UI } from "../ui/UI";
import { WorldController } from "../components/WorldController";
import { createContext } from "react";
import * as THREE from "three/webgpu";
import { input, keyBindings } from "../core/input/controls";
import { useShortcut } from "@core/hooks/useShortcut";
import { AudioLoader } from 'three';
import { ROSE_TEXTURES } from "../components/Rose/core/config";
import { BODY_TEXTURE_PATHS, DETAIL_TEXTURE_PATHS, MODEL_PATHS } from '../components/character/config';


useLoader.preload(AudioLoader,
    ['/audio/fs_grass1.mp3',
        '/audio/fs_grass2.mp3',
        '/audio/fs_grass3.mp3',
        '/audio/fs_grass4.mp3',
        '/audio/fs_grass5.mp3']);

useLoader.preload(AudioLoader, ['/audio/wave01.mp3']);

useGLTF.preload(MODEL_PATHS);

preloadVATAssets('/vat/Rose_meta.json');
preloadVATAssets('/vat/RoseLowPoly_meta.json');

export const BeamSceneContext = createContext<THREE.Scene | null>(null);

export default function App() {
    const beamScene = useMemo(() => new THREE.Scene(), []);
    const [dpr, setDpr] = useState(1.5);
    // The scene uses three/webgpu TSL materials (MeshStandardNodeMaterial).
    // A *plain* WebGLRenderer cannot compile those (resolveIncludes gets an
    // undefined shader string and throws every frame -> black canvas), and
    // native WebGPU on phones is unreliable (real mobile devices report an
    // adapter, so WebGPURenderer picks the WebGPU backend there and can
    // render black/hang; headless CI shows no adapter and hides this).
    // Solution: phones force WebGPURenderer's `forceWebGL: true`, which uses
    // three's WebGLBackend - it compiles TSL correctly and WebGL is rock
    // solid on phones. Desktops keep native WebGPU and fall back to the same
    // WebGLBackend if init rejects.
    const [webglFallback, setWebglFallback] = useState(() => {
        if (typeof navigator === 'undefined') return false;
        return /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);
    });

    const toggleCameraMode = useGameStore((state) => state.toggleCameraMode);
    const setGpuError = useGameStore((state) => state.setGpuError);
    const setAudioListener = useGameStore((state) => state.setAudioListener);
    const gpuError = useGameStore((state) => state.gpuError);
    const isMobile = useGameStore((state) => state.isMobile);
    const quality = useGameStore((state) => state.quality);
    const setQuality = useGameStore((state) => state.setQuality);

    // Auto-degrade quality on mobile / weak devices
    useEffect(() => {
        if (isMobile && quality === 'high') {
            setQuality('low');
        }
    }, [isMobile, quality, setQuality]);

    // Lower initial DPR on mobile for a smoother start
    useEffect(() => {
        if (isMobile) {
            setDpr(1);
        }
    }, [isMobile]);

    // Check WebGPU support on mount. WebGPURenderer itself auto-falls back to
    // its WebGL2 backend when WebGPU is absent, so a missing adapter is NOT
    // fatal: we keep WebGPURenderer (which compiles TSL correctly) and only
    // fall back to a raw WebGLRenderer if init() actually rejects.
    useEffect(() => {
        const checkWebGPU = async () => {
            if (!navigator.gpu) {
                console.warn("WebGPU is not supported in this browser - WebGPURenderer will use its WebGL2 backend");
                return;
            }
            try {
                const adapter = await navigator.gpu.requestAdapter();
                if (!adapter) {
                    console.warn("No WebGPU adapter available - WebGPURenderer will use its WebGL2 backend");
                    return;
                }
                console.log('WebGPU initialized successfully');
                setGpuError(null); // Clear any previous errors
            } catch (e) {
                console.warn("WebGPU adapter check failed - WebGPURenderer will use its WebGL2 backend:", e);
            }
        };
        checkWebGPU();
    }, [setGpuError]);

    useShortcut('c', () => {
        toggleCameraMode();
    });

    return <>
        <LevaWrapper collapsed={true} initialHidden={true} />
        <DeviceDetector />
        <UI />
        <KeyboardMapper input={input} keyMap={keyBindings} />


        {!gpuError && (
            <Canvas
                camera={{
                    fov: 45,
                    near: 1,
                    far: 200000,
                    position: [20, 20, 30]
                }}
                gl={(defaultProps) => {
                    const common: any = {
                        powerPreference: "high-performance",
                        antialias: true,
                        alpha: true,
                    };
                    // Phones always use the WebGL2 backend (forceWebGL) so TSL
                    // compiles reliably; desktops try native WebGPU first and
                    // fall back to the same WebGL2 backend on failure. A plain
                    // WebGLRenderer is never used because it cannot compile the
                    // character's TSL materials.
                    const renderer = new WebGPURenderer({ ...defaultProps, ...common, forceWebGL: webglFallback });
                    renderer.setClearColor('#cfe0ee');
                    renderer.autoClear = true;
                    renderer.sortObjects = false;
                    return renderer.init().then(() => renderer).catch((err) => {
                        console.warn("WebGPU renderer init failed - retrying with WebGL2 backend:", err);
                        const glRenderer = new WebGPURenderer({ ...defaultProps, ...common, forceWebGL: true });
                        glRenderer.setClearColor('#cfe0ee');
                        glRenderer.autoClear = true;
                        glRenderer.sortObjects = false;
                        return glRenderer.init().then(() => glRenderer).catch((err2) => {
                            console.error("Both WebGPU and WebGL2 backends failed to init:", err2);
                            setGpuError('GPU_INIT_FAILED');
                            throw err2;
                        });
                    });
                }}
                dpr={dpr}
            >
                <Suspense fallback={null}>
                    <KTX2Preloader paths={ROSE_TEXTURES} />
                    <KTX2Preloader paths={BODY_TEXTURE_PATHS} />
                    <KTX2Preloader paths={DETAIL_TEXTURE_PATHS} />
                </Suspense>

                <AudioManager onListenerCreated={setAudioListener} />

                <PerformanceMonitor
                    bounds={() => [28, 32]}
                    onFallback={() => setDpr(1)}
                    onChange={({ factor }) => {
                        const targetDpr = 1 + 1 * factor;
                        setDpr(targetDpr);
                        // console.log("factor", factor, "target DPR", targetDpr);
                    }}
                />

                <BeamSceneContext.Provider value={beamScene}>
                    <WorldController />

                    <Suspense fallback={null}>
                        <color attach="background" args={['#cfe0ee']} />
                        <CameraViewControl />
                        <Environment
                            files="/textures/potsdamer_platz_1k_nb.hdr"
                            environmentIntensity={0.5}
                        />
                        <DirectionalLight />
                        {!webglFallback && <Effects />}
                    </Suspense>
                </BeamSceneContext.Provider>
            </Canvas>
        )}
    </>
}
